// dragrect.ts ↔ game/dragrect.cpp + game/vec2d.h — the rubber-band selection
// rect the finger UI tracks (pt0 lower left, points ccw; the rect rotates as
// fingers move, so it is a point + two edge vectors, all doubles). PRESENTATION
// float: gesture chrome, never sim-affecting.
//
// The C++ passes DragRect BY VALUE (sprite get/set copies) — Clone() is that
// copy; callers clone where the C++ declares a value local.

// game/vec2d.h:8-11
export interface DPoint {
  x: number;
  y: number;
}

// game/vec2d.h:13-81
export class Vec2d {
  dx: number;
  dy: number;

  // game/vec2d.h:15-28 (the (dx, dy) and default ctors)
  constructor(dx = 0, dy = 0) {
    this.dx = dx;
    this.dy = dy;
  }

  // game/vec2d.h:20-23 — Vec2d(const DPoint& pt0, const DPoint& pt1)
  static FromPoints(pt0: DPoint, pt1: DPoint): Vec2d {
    return new Vec2d(pt1.x - pt0.x, pt1.y - pt0.y);
  }

  // game/vec2d.h:35-38
  unit(): Vec2d {
    const magT = this.mag();
    return new Vec2d(this.dx / magT, this.dy / magT);
  }

  // game/vec2d.h:40-42
  mag(): number {
    return Math.sqrt(this.dx * this.dx + this.dy * this.dy);
  }

  // game/vec2d.h:44-46
  scale(amount: number): Vec2d {
    return new Vec2d(this.dx * amount, this.dy * amount);
  }

  // game/vec2d.h:48-53
  add(pt: DPoint): DPoint {
    return { x: pt.x + this.dx, y: pt.y + this.dy };
  }

  // game/vec2d.h:55-57
  dot(v: Vec2d): number {
    return this.dx * v.dx + this.dy * v.dy;
  }

  // game/vec2d.h:59-61
  project(v: Vec2d): number {
    return this.unit().dot(v);
  }

  // game/vec2d.h:63-65
  flip90(): Vec2d {
    return new Vec2d(-this.dy, this.dx);
  }

  // game/vec2d.h:67-69
  flip180(): Vec2d {
    return new Vec2d(-this.dx, -this.dy);
  }

  // game/vec2d.h:71-73
  flip270(): Vec2d {
    return new Vec2d(this.dy, -this.dx);
  }

  // game/vec2d.h:75-77
  isnan(): boolean {
    return Number.isNaN(this.dx) || Number.isNaN(this.dy);
  }
}

// <float.h> FLT_MAX (dragrect.cpp:127 hit-test best-distance sentinel)
const FLT_MAX = 3.402823466e38;

// game/dragrect.h:12-38 + game/dragrect.cpp
export class DragRect {
  private pt0_: DPoint = { x: 0, y: 0 };
  private v01_ = new Vec2d();
  private v02_ = new Vec2d();

  /** C++ value-copy (DragRect drc = ...). */
  Clone(): DragRect {
    const drc = new DragRect();
    drc.pt0_ = { x: this.pt0_.x, y: this.pt0_.y };
    drc.v01_ = new Vec2d(this.v01_.dx, this.v01_.dy);
    drc.v02_ = new Vec2d(this.v02_.dx, this.v02_.dy);
    return drc;
  }

  // game/dragrect.cpp:7-11
  Init(pt0: DPoint, pt1: DPoint, pt2: DPoint): void {
    this.pt0_ = { x: pt0.x, y: pt0.y };
    this.v01_ = Vec2d.FromPoints(pt0, pt1);
    this.v02_ = Vec2d.FromPoints(pt0, pt2);
  }

  // game/dragrect.cpp:13-19
  private TrackPoints01(pt0: DPoint, pt1: DPoint): void {
    // width variable, height constant, theta variable
    const dy = this.v01_.flip90().project(this.v02_);
    this.pt0_ = { x: pt0.x, y: pt0.y };
    this.v01_ = Vec2d.FromPoints(pt0, pt1);
    this.v02_ = Vec2d.FromPoints(pt0, this.v01_.flip90().unit().scale(dy).add(pt1));
  }

  // game/dragrect.cpp:21-31
  private TrackPoints02(pt0: DPoint, pt2: DPoint): void {
    // width variable, height variable, theta constant
    const fdy = this.v02_.project(this.v01_) / this.v02_.mag();
    const fdx =
      Vec2d.FromPoints(
        this.v02_
          .unit()
          .scale(this.v02_.mag() * fdy)
          .add(this.pt0_),
        this.v01_.add(this.pt0_),
      ).mag() / this.v02_.mag();
    this.v02_ = Vec2d.FromPoints(pt0, pt2);
    const ptT = this.v02_
      .unit()
      .scale(this.v02_.mag() * fdy)
      .add(pt0);
    const pt1 = this.v02_
      .flip270()
      .unit()
      .scale(this.v02_.mag() * fdx)
      .add(ptT);
    this.v01_ = Vec2d.FromPoints(pt0, pt1);
    this.pt0_ = { x: pt0.x, y: pt0.y };
  }

  // game/dragrect.cpp:33-40
  private TrackPoints03(pt0: DPoint, pt3: DPoint): void {
    // width constant, height variable, theta variable
    const v03 = Vec2d.FromPoints(pt0, pt3);
    const pt1 = v03.flip270().unit().scale(this.v01_.mag()).add(pt0);
    this.pt0_ = { x: pt0.x, y: pt0.y };
    this.v01_ = Vec2d.FromPoints(pt0, pt1);
    this.v02_ = Vec2d.FromPoints(pt0, v03.add(pt1));
  }

  // game/dragrect.cpp:42-49
  private TrackPoints12(pt1: DPoint, pt2: DPoint): void {
    // width constant, height variable, theta variable
    const v12 = Vec2d.FromPoints(pt1, pt2);
    const pt0 = v12.flip90().unit().scale(this.v01_.mag()).add(pt1);
    this.pt0_ = pt0;
    this.v01_ = Vec2d.FromPoints(pt0, pt1);
    this.v02_ = Vec2d.FromPoints(pt0, pt2);
  }

  // game/dragrect.cpp:51-62
  private TrackPoints13(pt1: DPoint, pt3: DPoint): void {
    // width variable, height variable, theta constant
    const fdy = this.v02_.project(this.v01_) / this.v02_.mag();
    const fdx =
      Vec2d.FromPoints(
        this.v02_
          .unit()
          .scale(this.v02_.mag() * fdy)
          .add(this.pt0_),
        this.v01_.add(this.pt0_),
      ).mag() / this.v02_.mag();
    const v13 = Vec2d.FromPoints(pt1, pt3);
    const ptT = v13
      .unit()
      .scale(v13.mag() * fdy)
      .add(pt1);
    const pt0 = v13
      .flip90()
      .unit()
      .scale(v13.mag() * fdx)
      .add(ptT);
    this.pt0_ = pt0;
    this.v01_ = Vec2d.FromPoints(pt0, pt1);
    this.v02_ = Vec2d.FromPoints(pt0, this.v01_.add(pt3));
  }

  // game/dragrect.cpp:64-71
  private TrackPoints23(pt2: DPoint, pt3: DPoint): void {
    // width variable, height constant, theta variable
    const dy = this.v01_.flip90().project(this.v02_);
    const v32 = Vec2d.FromPoints(pt3, pt2);
    this.pt0_ = v32.flip270().unit().scale(dy).add(pt3);
    this.v01_ = Vec2d.FromPoints(this.pt0_, v32.add(this.pt0_));
    this.v02_ = Vec2d.FromPoints(this.pt0_, pt2);
  }

  // game/dragrect.cpp:73-78
  private TrackPoint0(pt0: DPoint): void {
    // pt2 fixed, v01 direction locked
    this.v02_ = Vec2d.FromPoints(pt0, this.v02_.add(this.pt0_));
    this.v01_ = this.v01_.unit().scale(this.v01_.project(this.v02_));
    this.pt0_ = { x: pt0.x, y: pt0.y };
  }

  // game/dragrect.cpp:80-86
  private TrackPoint1(pt1: DPoint): void {
    // pt3 fixed, v01 direction locked
    const pt3 = this.v01_.flip180().add(this.v02_.add(this.pt0_));
    this.v01_ = this.v01_.unit().scale(-this.v01_.project(Vec2d.FromPoints(pt1, pt3)));
    this.pt0_ = this.v01_.flip180().add(pt1);
    this.v02_ = Vec2d.FromPoints(this.pt0_, this.v01_.add(pt3));
  }

  // game/dragrect.cpp:88-92
  private TrackPoint2(pt2: DPoint): void {
    // pt0 fixed, v01 direction locked
    this.v02_ = Vec2d.FromPoints(this.pt0_, pt2);
    this.v01_ = this.v01_.unit().scale(this.v01_.project(this.v02_));
  }

  // game/dragrect.cpp:94-101
  private TrackPoint3(pt3: DPoint): void {
    // pt1 fixed, v01 direction locked
    const pt1 = this.v01_.add(this.pt0_);
    const v13 = Vec2d.FromPoints(pt1, pt3);
    this.v01_ = this.v01_.unit().scale(-this.v01_.project(v13));
    this.pt0_ = this.v01_.flip180().add(pt1);
    this.v02_ = Vec2d.FromPoints(this.pt0_, this.v01_.add(pt3));
  }

  // game/dragrect.cpp:103-120
  GetPoints(apt: DPoint[]): void {
    apt[0] = { x: this.pt0_.x, y: this.pt0_.y };
    if (!this.v01_.isnan()) {
      apt[1] = this.v01_.add(this.pt0_);
    } else {
      apt[1] = { x: this.pt0_.x, y: this.pt0_.y };
    }
    if (!this.v02_.isnan()) {
      apt[2] = this.v02_.add(this.pt0_);
    } else {
      apt[2] = { x: this.pt0_.x, y: this.pt0_.y };
    }
    if (!this.v01_.isnan()) {
      apt[3] = this.v01_.flip180().add(apt[2]);
    } else {
      apt[3] = { x: apt[2].x, y: apt[2].y };
    }
  }

  // game/dragrect.cpp:122-143
  HitTest(pt: DPoint, pvOffset: Vec2d | null = null): number {
    const apt: DPoint[] = [];
    this.GetPoints(apt);

    let iBest = -1;
    let magBest = FLT_MAX;
    for (let i = 0; i < apt.length; i++) {
      const mag = Vec2d.FromPoints(pt, apt[i]).mag();
      if (mag < magBest) {
        magBest = mag;
        iBest = i;
      }
    }

    if (pvOffset !== null) {
      const v = Vec2d.FromPoints(pt, apt[iBest]);
      pvOffset.dx = v.dx;
      pvOffset.dy = v.dy;
    }

    // Use a mask in case this will reflect edge and corner hittesting
    // in the future.
    return 1 << iBest;
  }

  // game/dragrect.cpp:145-168 — NOTE: the C++ Rect fields are ints, so the
  // double point coords TRUNCATE on store (toward zero); kept verbatim
  GetBoundingRect(prc: { left: number; top: number; right: number; bottom: number }): void {
    prc.left = 9999;
    prc.right = -1;
    prc.top = 9999;
    prc.bottom = -1;

    const apt: DPoint[] = [];
    this.GetPoints(apt);

    for (let i = 0; i < apt.length; i++) {
      if (apt[i].x < prc.left) {
        prc.left = Math.trunc(apt[i].x);
      }
      if (apt[i].x > prc.right) {
        prc.right = Math.trunc(apt[i].x);
      }
      if (apt[i].y < prc.top) {
        prc.top = Math.trunc(apt[i].y);
      }
      if (apt[i].y > prc.bottom) {
        prc.bottom = Math.trunc(apt[i].y);
      }
    }
  }

  // game/dragrect.cpp:170-194
  PtIn(pt: DPoint): boolean {
    // Look at the z portion of the cross product on each side. The result
    // should be all negative or positive to be inside.

    const apt: DPoint[] = [];
    this.GetPoints(apt);

    const d0 =
      (pt.x - apt[0].x) * (apt[1].y - apt[0].y) - (pt.y - apt[0].y) * (apt[1].x - apt[0].x);
    const d1 =
      (pt.x - apt[1].x) * (apt[2].y - apt[1].y) - (pt.y - apt[1].y) * (apt[2].x - apt[1].x);
    const d2 =
      (pt.x - apt[2].x) * (apt[3].y - apt[2].y) - (pt.y - apt[2].y) * (apt[3].x - apt[2].x);
    const d3 =
      (pt.x - apt[3].x) * (apt[0].y - apt[3].y) - (pt.y - apt[3].y) * (apt[0].x - apt[3].x);

    if (d0 < 0.0 && d1 < 0.0 && d2 < 0.0 && d3 < 0.0) {
      return true;
    }
    if (d0 >= 0.0 && d1 >= 0.0 && d2 >= 0.0 && d3 >= 0.0) {
      return true;
    }

    return false;
  }

  // game/dragrect.cpp:196-236
  ScrollExpand(maskA: number, maskB: number, dx: number, dy: number): void {
    // This only applies when one finger is down. Imagine the map being
    // scrolled by dx, dy, and the scroll rect expanding. Basically, the
    // fixed point of the rect scrolls in the dx, dy direction.

    if (maskA !== 0 && maskB !== 0) {
      return;
    }

    // Simply "scroll" the fixed point

    let pt: DPoint;
    const apt: DPoint[] = [];
    this.GetPoints(apt);
    switch (maskA | maskB) {
      case 1:
        pt = apt[2];
        pt.x += dx;
        pt.y += dy;
        this.TrackPoint2(pt);
        break;
      case 2:
        pt = apt[3];
        pt.x += dx;
        pt.y += dy;
        this.TrackPoint3(pt);
        break;
      case 4:
        pt = apt[0];
        pt.x += dx;
        pt.y += dy;
        this.TrackPoint0(pt);
        break;
      case 8:
        pt = apt[1];
        pt.x += dx;
        pt.y += dy;
        this.TrackPoint1(pt);
        break;
    }
  }

  // game/dragrect.cpp:238-338
  TrackPoints(maskA: number, ptA: DPoint, maskB: number, ptB: DPoint): void {
    if (maskA === 0) {
      switch (maskB) {
        case 1:
          this.TrackPoint0(ptB);
          break;
        case 2:
          this.TrackPoint1(ptB);
          break;
        case 4:
          this.TrackPoint2(ptB);
          break;
        case 8:
          this.TrackPoint3(ptB);
          break;
        default:
          break;
      }
    } else if (maskA === 1) {
      switch (maskB) {
        case 0:
          this.TrackPoint0(ptA);
          break;
        case 1:
          this.TrackPoint0(ptA);
          break;
        case 2:
          this.TrackPoints01(ptA, ptB);
          break;
        case 4:
          this.TrackPoints02(ptA, ptB);
          break;
        case 8:
          this.TrackPoints03(ptA, ptB);
          break;
        default:
          break;
      }
    } else if (maskA === 2) {
      switch (maskB) {
        case 0:
          this.TrackPoint1(ptA);
          break;
        case 1:
          this.TrackPoints01(ptB, ptA);
          break;
        case 2:
          this.TrackPoint2(ptA);
          break;
        case 4:
          this.TrackPoints12(ptA, ptB);
          break;
        case 8:
          this.TrackPoints13(ptA, ptB);
          break;
        default:
          break;
      }
    } else if (maskA === 4) {
      switch (maskB) {
        case 0:
          this.TrackPoint2(ptA);
          break;
        case 1:
          this.TrackPoints02(ptB, ptA);
          break;
        case 2:
          this.TrackPoints12(ptB, ptA);
          break;
        case 4:
          this.TrackPoint2(ptA);
          break;
        case 8:
          this.TrackPoints23(ptA, ptB);
          break;
        default:
          break;
      }
    } else if (maskA === 8) {
      switch (maskB) {
        case 0:
          this.TrackPoint3(ptA);
          break;
        case 1:
          this.TrackPoints03(ptB, ptA);
          break;
        case 2:
          this.TrackPoints13(ptB, ptA);
          break;
        case 4:
          this.TrackPoints23(ptB, ptA);
          break;
        case 8:
          this.TrackPoint3(ptA);
          break;
        default:
          break;
      }
    }
  }
}
